import { Link } from "@ui/Atoms/Link/Link";
import type * as React from "react";

export type IconButtonVariant = "glass" | "outline" | "soft" | "solid";
export type IconButtonShape = "circle" | "square";

const VARIANTS: Record<IconButtonVariant, React.CSSProperties> = {
	glass: {
		background: "var(--surface-glass)",
		border: "1px solid var(--border-on-dark-strong)",
		color: "var(--content-inverted)",
	},
	outline: {
		background: "var(--surface-default)",
		border: "1px solid var(--border-strong)",
		color: "var(--content-primary)",
	},
	soft: {
		background: "var(--surface-subtle)",
		border: "none",
		color: "var(--content-muted)",
	},
	solid: {
		background: "var(--surface-inverted)",
		border: "none",
		color: "var(--content-inverted)",
	},
};

type OwnProps = {
	/** Accessible name for the control. */
	label: string;
	children: React.ReactNode;
	variant?: IconButtonVariant;
	shape?: IconButtonShape;
	size?: number;
};

type AsButton = OwnProps & {
	as?: "button";
} & React.ButtonHTMLAttributes<HTMLButtonElement>;
type AsAnchor = OwnProps & {
	as: "a";
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

export type IconButtonProps = AsButton | AsAnchor;

export function IconButton({
	label,
	children,
	variant = "outline",
	shape = "circle",
	size = 40,
	as = "button",
	style,
	...props
}: IconButtonProps) {
	const computed: React.CSSProperties = {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
		width: size,
		height: size,
		borderRadius:
			shape === "circle" ? "var(--radius-pill)" : "var(--radius-sm)",
		cursor: "pointer",
		transition: "background 0.15s, border-color 0.15s",
		...VARIANTS[variant],
		...style,
	};

	if (as === "a") {
		const anchorProps = props as React.AnchorHTMLAttributes<HTMLAnchorElement>;
		return (
			<Link
				href={anchorProps.href}
				aria-label={label}
				style={computed}
				{...anchorProps}
			>
				{children}
			</Link>
		);
	}

	return (
		<button
			type="button"
			aria-label={label}
			style={computed}
			{...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
		>
			{children}
		</button>
	);
}
